import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {
    Bundle,
    Transfer,
    TruckStub,
    useFullOpenTransfersForTruck,
    VenueStub,
    watchBundleByIdTqo
} from "../rpc/webrpc-client.gen.ts";
import {useQueries} from "@tanstack/react-query";
import {z} from "zod/v4";
import {cleanse, Ided, indexesOf, NADA} from "../util/arutil.tsx";
import {errorize} from "../bomb/errorize.tsx";
import {mergeUseQueriesWithInfill} from "./mergeUseQueriesWithInfill.tsx";
import {useMothership} from "./tqx.tsx";

export const BundledTransfer = z.object({
    ...Transfer.shape,
    bundle: Bundle,
})
export type BundledTransfer = z.infer<typeof BundledTransfer>;

export function useBundledFullOpenTransfersForTruck(args?: {
    truckId: string
}): InfallibleUseResult<BundledTransfer[]> {
    const mama = useMothership();
    const xfersTqrv = useFullOpenTransfersForTruck(args);
    const bundlesTqrv = useQueries(xfersTqrv.isSuccess
        ? {
            queries: xfersTqrv.data.map(xfer => watchBundleByIdTqo(mama, xfer && {bundleId: xfer.bundle.id})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});

    if (xfersTqrv.isPending || bundlesTqrv.isPending) {
        return pendingUseResult;
    }
    if (xfersTqrv.isError) {
        throw errorize(xfersTqrv.errors);
    }
    if (bundlesTqrv.isError) {
        throw errorize(bundlesTqrv.errors);
    }

    const ixen = indexesOf(xfersTqrv.data);
    const maybes = ixen.map(ix => {
        const xfer = xfersTqrv.data[ix];
        const bundle = bundlesTqrv.data[ix];
        if (xfer && bundle) {
            return BundledTransfer.parse({...xfer, bundle});
        } else {
            return undefined;
        }
    });
    const rv = cleanse(maybes);

    return successUseResult(rv);
}

export const VenuePlace = z.object({
    ...Ided.shape,
    truck: z.optional(z.never()),
    venue: VenueStub,
});

export type VenuePlace = z.infer<typeof VenuePlace>;

export function makeVenuePlace(venue: VenueStub): VenuePlace {
    return {id: `venue-${venue.id}`, venue};
}

export const TruckPlace = z.object({
    ...Ided.shape,
    truck: TruckStub,
    venue: z.optional(z.never()),
});
export type TruckPlace = z.infer<typeof TruckPlace>;

export function makeTruckPlace(truck: TruckStub): TruckPlace {
    return {id: `truck-${truck.id}`, truck};
}